<script setup lang="ts">
import { storeToRefs } from 'pinia'

import ProfileActivity from './about/Activity.vue'
import ProfileInfo from './about/Info.vue'
import ProfileMatchingTags from './about/MatchingTags.vue'
import ProfileVerifiedBadge from './about/VerifiedProfileBadge.vue'

import MismatchInfo from '~/components/influencer/profile/about/MismatchInfo.vue'
import MainContent from '~/components/influencer/profile/CuratorProfileMainContent.vue'

import { useComputeMismatches } from '~/composables/useComputeMismatches'

import { useDraftStore } from '~/stores/draft'
import { useDraftTrackStore } from '~/stores/draftTrack'
import { useDraftTrackTagsStore } from '~/stores/draftTrackTags'
import { useMiscResizeStore } from '~/stores/miscResize'
import { useTagStore } from '~/stores/tag'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'

import { arrayUniqueValues } from '~/utils/arrays'

import { Breakpoints } from '~/enums/breakpoints'

import type Tag from '~/entities/tag'
import type {
  Influencer,
  InfluencerLikedNeutralHatedTags,
  InfluencerPreferences,
  InfluencerPreferenceTags,
} from '~/types/influencer'
import type TagInformation from '~/types/tagInformations'

type Props = {
  influencer: Influencer
}

const props = defineProps<Props>()

const { t } = useI18n()
const { computeMismatches } = useComputeMismatches()
const { mismatches } = computeMismatches('profile', props.influencer)

const orderedTargets: readonly (keyof InfluencerPreferenceTags)[] = [
  'liked',
  'neutral',
  'hated',
]

/**
 * This serve no purpose right now ?
 */
const displayMismatches = computed(() => mismatches.value.length)
const hasStrongMismatches = computed(() =>
  mismatches.value.some(({ weakMismatch }) => !weakMismatch),
)

const { tags: DRAFT_TAG_IDS } = storeToRefs(useDraftStore())
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { BAND_DATA } = storeToRefs(useUserBandStore())
const { IS_BAND } = storeToRefs(useUserStore())
const { identity: DRAFT_TRACK_IDENTITY_TAGS } = storeToRefs(
  useDraftTrackTagsStore(),
)
const { release_date: DRAFT_TRACK_RELEASE_DATE } =
  storeToRefs(useDraftTrackStore())
const { GET_TAGS_FROM_IDS, GET_TYPES_FROM_TAG_IDS } = useTagStore()

const genresConfiguration = computed<InfluencerPreferences>(() => {
  if (!props.influencer.tags) {
    return {
      liked: [],
      neutral: [],
      hated: [],
    } satisfies InfluencerPreferences
  }

  return orderedTargets.reduce<InfluencerPreferences>((acc, target) => {
    const typeNames: (keyof InfluencerLikedNeutralHatedTags)[] = [
      'subgenre',
      'mood',
      'format',
      'artist_kind',
    ]
    const additionalTagInformations = typeNames.reduce<TagInformation[]>(
      (accumulator, typeName) => {
        const partialTagInformations = GET_TAGS_FROM_IDS([
          ...new Set((props.influencer.tags[target] ?? {})[typeName] ?? []),
        ]).map((tag) => {
          return {
            typeName,
            tag,
          } satisfies TagInformation
        })
        if (!hasContent(partialTagInformations)) return accumulator

        return [...accumulator, ...partialTagInformations]
      },
      [],
    )
    if (!hasContent(additionalTagInformations)) return acc

    acc[target] = additionalTagInformations
    return acc
  }, {} as InfluencerPreferences)
})

const combinedInfluencerTagsToMatch = computed<TagInformation[]>(() => {
  return GET_TAGS_FROM_IDS(combinedInfluencerTagsIdsToMatch.value).map(
    (tag) => {
      return { typeName: 'subgenre', tag }
    },
  )
})

const combinedInfluencerTagsIdsToMatch = computed<number[]>(() => {
  if (!genresConfiguration.value) return []

  const liked =
    genresConfiguration.value.liked?.filter(
      (tagInfo) => tagInfo.typeName === 'subgenre',
    ) ?? []
  const neutral =
    genresConfiguration.value.neutral?.filter(
      (tagInfo) => tagInfo.typeName === 'subgenre',
    ) ?? []

  return arrayUniqueValues([
    ...liked.map((tagInfo) => tagInfo.tag.id),
    ...neutral.map((tagInfo) => tagInfo.tag.id),
    ...genresAcceptedMostOftenIds.value,
  ])
})

const genresAcceptedMostOftenIds = computed<number[]>(
  () => props.influencer.latest_stats?.genres_accepted_often ?? [],
)

const hasMatches = computed<boolean>(
  () =>
    !!(
      matchingGenres.value.length > 0 ||
      matchingNationalities.value.length > 0 ||
      matchingLanguages.value.length > 0 ||
      matchingTrackRelease.value
    ),
)

const matchingGenres = computed<TagInformation[]>(() => {
  const artistGenres = GET_TAGS_FROM_IDS(
    BAND_DATA.value?.tags?.identity.subgenre || [],
  ).map((genre) => genre.id)

  const draftGenres = getMatchingDraftGenreTags()

  const matches = [...combinedInfluencerTagsToMatch.value].filter(
    (tagInfo) =>
      artistGenres.includes(tagInfo.tag.id) ||
      draftGenres.includes(tagInfo.tag.id),
  )

  return [...matches].filter((tagInfo) =>
    combinedInfluencerTagsIdsToMatch.value.includes(tagInfo.tag.id),
  )
})

const matchingNationalities = computed<Tag[]>(() => {
  const artistNationalities = BAND_DATA.value?.tags?.identity.country || []

  const curatorNationalities = props.influencer.tags?.exclusivity.country
  const curatorAcceptsAny = isAcceptAny(curatorNationalities)

  if (curatorAcceptsAny) return GET_TAGS_FROM_IDS(artistNationalities)

  const matches = artistNationalities.filter((tagId) =>
    curatorNationalities.includes(tagId),
  )

  return GET_TAGS_FROM_IDS(matches ?? [])
})

const matchingLanguages = computed<Tag[]>(() => {
  const artistLanguages = DRAFT_TRACK_IDENTITY_TAGS.value.lyrics_lang || []
  const curatorLanguages = props.influencer.tags.exclusivity.lyrics_lang || []

  const curatorAcceptsAny = isAcceptAny(curatorLanguages)

  if (curatorAcceptsAny) return GET_TAGS_FROM_IDS(artistLanguages)

  const matches = artistLanguages.filter((tagId) =>
    curatorLanguages.includes(tagId),
  )

  return GET_TAGS_FROM_IDS(matches ?? [])
})

const matchingTrackRelease = computed<string>(() => {
  const curatorTrackRelease =
    GET_TAGS_FROM_IDS(props.influencer.tags.exclusivity.track_age)[0]?.name ||
    ''

  const trackReleaseDate = DRAFT_TRACK_RELEASE_DATE.value
    ? new Date(DRAFT_TRACK_RELEASE_DATE.value)
    : new Date()
  const isTrackReleased = new Date() > trackReleaseDate

  const acceptsAny = curatorTrackRelease === '_all_'
  const acceptsReleased =
    curatorTrackRelease === 'released-tracks' || acceptsAny
  const acceptsUnreleased =
    curatorTrackRelease === 'unreleased-tracks' || acceptsAny

  if (acceptsAny && !DRAFT_TRACK_RELEASE_DATE.value)
    return t(`tags.track_age._all_`)
  else if (acceptsReleased && isTrackReleased)
    return t(`tags.track_age.released-tracks`)
  else if (acceptsUnreleased && !isTrackReleased)
    return t(`tags.track_age.unreleased-tracks`)

  return ''
})

const showMatchingTags = computed<boolean>(
  () => IS_BAND.value && !hasStrongMismatches.value && hasMatches.value,
)

const showVerifiedBadge = computed<boolean>(() => props.influencer.authorized)
const hasBadgeTags = computed<boolean>(
  () =>
    (props.influencer.groover_note?.length ?? 0) > 0 ||
    (props.influencer.tags?.identity.influencer_badge?.length ?? 0) > 0 ||
    Number(props.influencer.cost) > 2,
)

function getMatchingDraftGenreTags(): number[] {
  const genreTagTypes = GET_TYPES_FROM_TAG_IDS(
    DRAFT_TAG_IDS.value ?? [],
  ).filter((tagType) => tagType.name === 'subgenre')

  const genreTagIds = genreTagTypes.length > 0 ? genreTagTypes[0].tag_ids : []

  const draftGenreTagIds = DRAFT_TAG_IDS.value.filter((tagId) =>
    genreTagIds.includes(tagId),
  )

  const matchingDraftGenreTagIds = draftGenreTagIds.filter((tagId) =>
    combinedInfluencerTagsIdsToMatch.value.includes(tagId),
  )

  return matchingDraftGenreTagIds
}
function hasContent(tagInformations: TagInformation[]): boolean {
  return Boolean(tagInformations.length)
}

function isAcceptAny(tags: number[]): boolean {
  return tags?.length === 1 && GET_TAGS_FROM_IDS(tags)[0]?.name === '_all_'
}
</script>

<template>
  <article class="influencerProfileBodyMainWrapper mainParentContainer">
    <div v-if="SCREEN_WIDTH >= Breakpoints.lg">
      <div class="splitScreen">
        <div class="tw-col-span-8 tw-flex tw-flex-col tw-gap-6">
          <ProfileMatchingTags
            v-if="showMatchingTags"
            :genres="matchingGenres"
            :nationalities="matchingNationalities"
            :languages="matchingLanguages"
            :track-release="matchingTrackRelease"
          />
          <div v-if="displayMismatches" class="tw-flex tw-flex-col tw-gap-4">
            <div
              v-for="{ mismatchText, weakMismatch } in mismatches"
              :key="mismatchText"
            >
              <MismatchInfo
                :key="mismatchText"
                :mismatch-text="mismatchText"
                class="mismatchInfo"
                :is-error="!weakMismatch"
              />
            </div>
          </div>
          <MainContent
            :influencer="influencer"
            :genres-configuration="genresConfiguration"
          >
            <template #post-content>
              <slot name="post-content" />
            </template>
          </MainContent>
        </div>

        <div class="infosWrapper">
          <template v-if="showVerifiedBadge">
            <ProfileVerifiedBadge />
            <hr class="tw-my-6 tw-h-px tw-border-0 tw-bg-gray-300" />
          </template>
          <template v-if="hasBadgeTags">
            <ProfileInfo :influencer="influencer" />
            <hr class="tw-my-6 tw-h-px tw-border-0 tw-bg-gray-300" />
          </template>
          <ProfileActivity :influencer="influencer" />
        </div>
      </div>
    </div>
    <div v-else class="fullScreen">
      <ProfileMatchingTags
        v-if="showMatchingTags"
        :genres="matchingGenres"
        :nationalities="matchingNationalities"
        :languages="matchingLanguages"
        :track-release="matchingTrackRelease"
      />
      <div class="tw-flex tw-flex-col tw-gap-4">
        <div
          v-for="{ mismatchText, weakMismatch } in mismatches"
          :key="mismatchText"
        >
          <MismatchInfo
            :key="mismatchText"
            :mismatch-text="mismatchText"
            class="mismatchInfo"
            :is-error="!weakMismatch"
          />
        </div>
      </div>
      <div v-if="SCREEN_WIDTH < Breakpoints.md" class="infosWrapper">
        <template v-if="hasBadgeTags">
          <ProfileInfo :influencer="influencer" />
          <hr class="tw-my-6 tw-h-px tw-border-0 tw-bg-gray-300" />
        </template>
        <ProfileActivity :influencer="influencer" />
        <template v-if="showVerifiedBadge">
          <hr class="tw-my-6 tw-h-px tw-border-0 tw-bg-gray-300" />
          <ProfileVerifiedBadge />
        </template>
      </div>
      <div v-else>
        <div class="infosWrapperSecondary">
          <div v-if="hasBadgeTags" class="tw-w-6/12 tw-pr-6">
            <ProfileInfo :influencer="influencer" />
          </div>
          <div class="tw-w-6/12">
            <ProfileActivity :influencer="influencer" />
            <ProfileVerifiedBadge v-if="showVerifiedBadge" class="tw-mt-6" />
          </div>
        </div>
      </div>
      <MainContent
        :influencer="influencer"
        :genres-configuration="genresConfiguration"
      >
        <template #post-content>
          <slot name="post-content" />
        </template>
      </MainContent>
    </div>
  </article>
</template>

<style lang="scss" scoped>
@media screen and (min-width: 1024px) {
  .splitScreen {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: var(--grid-gap);
  }
  .infosWrapper {
    grid-column: 10 / span 3;
    height: min-content;
  }
}

.influencerProfileBodyMainWrapper {
  @apply tw-py-10;
  & > * {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-10;
  }
}

.fullScreen {
  @apply tw-relative tw-flex tw-w-full tw-flex-col;
}

.infosWrapper {
  @apply tw-relative tw-flex tw-w-full tw-flex-col;
  & > hr:first-child {
    @apply tw-hidden;
  }
}

.infosWrapperSecondary {
  @apply tw-relative tw-flex tw-w-full;
}
</style>
